import React, { useState } from 'react';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { view } from '@forge/bridge';

import { OptionItemObject } from '../../../../../src/shared_models/interfaces';
import storageApi from '../../api/storageApi';
import { toCsv, toJson } from '../../utils/configExportMappers';
import { getFieldTreeData, getLevelsInfoData } from '../../utils/levelModelUtils';
import { downloadCsvFile, downloadJsonFile } from '../../utils/utils';

import { ParentOptionData } from './interfaces';
import LevelsBreadcrumbs from './LevelsBreadcrumbs';
import { TopBarWrapper } from './styles';

type Props = {
    currentNestingLevel: number;
    onBreadcrumbClick: (depthLevel: number) => void;
    onFieldsRequiredClick: () => void;
    onImportConfigClick: () => void;
    currentLevelParents?: ParentOptionData[];
};

const TopBar: React.FC<Props> = ({
    onBreadcrumbClick,
    currentNestingLevel,
    currentLevelParents,
    onFieldsRequiredClick,
    onImportConfigClick,
}) => {
    const [isPreparingExport, setIsPreparingExport] = useState(false);

    const withTryCatch = async (call: () => Promise<void>) => {
        try {
            setIsPreparingExport(true);
            await call();
        } catch (error) {
            console.error(error);
        } finally {
            setIsPreparingExport(false);
        }
    };

    const handleExportCsv = async () =>
        withTryCatch(async () => {
            const { data, fieldId, levelsInfo } = await fetchExportContent();
            downloadCsvFile(toCsv(data, levelsInfo), `${fieldId}_config`);
        });

    const handleExportJson = async () =>
        withTryCatch(async () => {
            const { data, fieldId, levelsInfo } = await fetchExportContent();
            downloadJsonFile(toJson(data, levelsInfo), `${fieldId}_config`);
        });

    const fetchExportContent = async () => {
        const { fieldId, configurationId } = (await view.getContext()).extension;
        const loadedData = await storageApi.getConfig(fieldId, configurationId.toString());
        const filteredTreeData = getFieldTreeData(loadedData);
        return {
            data: filteredTreeData as OptionItemObject[],
            levelsInfo: getLevelsInfoData(loadedData),
            fieldId,
        };
    };

    return (
        <TopBarWrapper>
            <LevelsBreadcrumbs
                onClick={onBreadcrumbClick}
                currentNestingLevel={currentNestingLevel}
                currentLevelParents={currentLevelParents}
            />
            <DropdownMenu
                trigger='Settings'
                placement='bottom-end'
                testId='settings-btn'
                isLoading={isPreparingExport}
            >
                <DropdownItemGroup>
                    <DropdownItem onClick={onFieldsRequiredClick}>Set fields required</DropdownItem>
                </DropdownItemGroup>
                <DropdownItemGroup hasSeparator>
                    <DropdownItem onClick={handleExportCsv}>Export CSV</DropdownItem>
                    <DropdownItem onClick={handleExportJson}>Export JSON</DropdownItem>
                    <DropdownItem onClick={onImportConfigClick}>Import configuration</DropdownItem>
                </DropdownItemGroup>
            </DropdownMenu>
        </TopBarWrapper>
    );
};

export default TopBar;
